/*
 * ExplorationReport.js — Guided builder (v2)
 *
 * Redesign highlights over v1:
 *   - Single guided flow (5 numbered steps) instead of Variables + Settings split.
 *   - Live plain-English "Your report" recap that mirrors builder state in real time.
 *   - Field picker modal (⌘K to open) with search + All/Dimensions/Metrics tabs
 *     that adds fields to the correct shelf and shows which fields are already in use.
 *   - Popular Templates gallery (one-click reports) that populates the actual
 *     guided-builder state — templates are a starting point, not a separate type.
 *   - Live auto-update on any builder change (no manual Run Report button); the
 *     table footer shows an "Updated just now · ⟳" status pill instead.
 *   - Smart Builder (Beta) reserved as a placeholder card; a future release will
 *     wire it to Claude Sonnet 5 or similar.
 *
 * Backwards-compatible: reads and writes the SAME `definition` shape used by v1
 * so every previously saved report loads without conversion.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import SmartBuilder from '../components/SmartBuilder';
import { toast } from 'sonner';
import {
  Plus, X, Trash2, Copy, Save, Download, Search, Compass, Sparkles,
  RefreshCw, Share2, Lock, ArrowDown, ArrowUp, ChevronDown, Calendar,
  Layers, Filter as FilterIcon, BarChart3, Users, Package, Tag, Grid,
  MessageSquare, Zap, Target, TrendingUp,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================================
// Utilities
// ============================================================================
const authHeaders = () => {
  const token = localStorage.getItem('trace_session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const fmtCurrency = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 10000000) return '₹' + (n / 10000000).toFixed(2) + 'Cr';
  if (Math.abs(n) >= 100000) return '₹' + (n / 100000).toFixed(2) + 'L';
  if (Math.abs(n) >= 1000) return '₹' + (n / 1000).toFixed(1) + 'K';
  return '₹' + n.toFixed(0);
};
const fmtValue = (v, fmt) => {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (fmt === 'currency') return fmtCurrency(n);
  if (fmt === 'percent') return n.toFixed(2) + '%';
  if (fmt === 'ratio') return n.toFixed(2) + 'x';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// A "just now / N min ago" ticker for the freshness indicator.
const useRelativeTime = (ts) => {
  const [_, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force(x => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!ts) return '';
  const diff = Math.max(0, (Date.now() - ts) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

// ============================================================================
// Report skeleton + templates
// ============================================================================
const emptyDefinition = () => ({
  technique: 'pivot',           // v2 defaults to pivot so "Compare across" always works
  date_range: { start: daysAgoISO(30), end: todayISO() },
  dimensions_rows: [],
  dimensions_cols: [],
  metrics: [],
  filters: [],
  sort: [],
  limit: 100,
});

// Popular Templates — each recipe fills the whole builder state.
const TEMPLATES = [
  { id: 'channel',      icon: BarChart3, title: 'Channel Performance', desc: 'Compare performance across channels over time',
    caption: 'Channel · Month · Revenue, Spend, ROI',
    def: { rows: ['channel'], cols: ['month'], metrics: ['total_revenue', 'ad_spend_gst', 'roi'] } },
  { id: 'brand',        icon: Tag,       title: 'Brand Performance',   desc: 'Track brand performance over time',
    caption: 'Brand · Month · Revenue, ROI',
    def: { rows: ['brand'], cols: ['month'], metrics: ['total_revenue', 'roi'] } },
  { id: 'campaign',     icon: Target,    title: 'Campaign Comparison', desc: 'Compare campaigns across key metrics',
    caption: 'Campaign · Month · Spend, ROI, ACOS',
    def: { rows: ['campaign_name'], cols: ['month'], metrics: ['ad_spend_gst', 'roi', 'acos'] } },
  { id: 'adtype',       icon: Layers,    title: 'Ad Type Analysis',    desc: 'Understand performance by ad type',
    caption: 'Ad Type · Month · Revenue, Spend',
    def: { rows: ['ad_type'], cols: ['month'], metrics: ['total_revenue', 'ad_spend_gst'] } },
  { id: 'marketplace',  icon: Package,   title: 'Marketplace View',    desc: 'Compare marketplaces performance',
    caption: 'Channel · Month · Revenue, ROI',
    def: { rows: ['channel'], cols: ['month'], metrics: ['total_revenue', 'roi'] } },
  { id: 'funnel',       icon: TrendingUp, title: 'Funnel Analysis',    desc: 'Analyze funnel metrics at each stage',
    caption: 'Funnel Stage · Month · Impressions, CVR',
    def: { rows: ['funnel_stage'], cols: ['month'], metrics: ['views', 'cvr'] } },
];

// Icon lookup for the field picker.
const DIM_ICON = { date: Calendar, week: Calendar, month: Calendar, day_of_week: Calendar,
  channel: Layers, ad_type: Layers, ad_product: Package, report_type: Grid,
  brand: Tag, campaign_name: Target, ad_group: Users, funnel_stage: TrendingUp };
const METRIC_ICON = { ad_spend: Zap, ad_spend_gst: Zap, total_revenue: BarChart3, views: BarChart3,
  clicks: BarChart3, total_converted_units: Package, roi: TrendingUp, acos: TrendingUp,
  ctr: TrendingUp, cvr: TrendingUp, cpc: TrendingUp, cpm: TrendingUp };

// ============================================================================
// PAGE
// ============================================================================
export default function ExplorationReport({ user }) {
  const [schema, setSchema] = useState({ dimensions: [], metrics: [] });
  const [reportList, setReportList] = useState([]);
  const [activeReportId, setActiveReportId] = useState(null);
  const [reportName, setReportName] = useState('New Exploration');
  const [reportShared, setReportShared] = useState(false);
  const [definition, setDefinition] = useState(emptyDefinition());
  const [result, setResult] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Field picker modal state
  const [picker, setPicker] = useState({ open: false, target: 'metric' }); // 'metric' | 'row' | 'col'
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerTab, setPickerTab] = useState('all'); // 'all' | 'dim' | 'metric'
  const pickerInputRef = useRef(null);

  // ---- Bootstrap ----
  useEffect(() => {
    (async () => {
      try {
        const [sch, rep] = await Promise.all([
          fetch(`${API}/api/exploration/schema`, { headers: authHeaders() }).then(r => r.json()),
          fetch(`${API}/api/exploration/reports`, { headers: authHeaders() }).then(r => r.json()),
        ]);
        setSchema(sch);
        setReportList(rep.reports || []);
      } catch { toast.error('Failed to load exploration schema.'); }
    })();
  }, []);

  // ---- Live auto-run on any definition change (debounced) ----
  const debounceRef = useRef(null);
  const runQuery = useCallback(async (defn) => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/exploration/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(defn),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResult(data); setLastRunAt(Date.now());
    } catch (err) {
      setError(err.message || 'Query failed'); setResult(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const ready = definition.metrics.length > 0 && definition.dimensions_rows.length > 0
      && (definition.technique === 'freeform' || definition.dimensions_cols.length > 0 || definition.metrics.length > 0);
    if (!ready) { setResult(null); return; }
    // Auto-fall back to a flat/grouped technique when the user hasn't picked
    // any "Compare across" dimension. The backend requires a pivot_col for the
    // 'pivot' technique, so without one we send 'grouped' and render the flat
    // single-row-per-dimension layout with a Totals row.
    const effectiveDef = (definition.technique === 'pivot' && definition.dimensions_cols.length === 0)
      ? { ...definition, technique: 'freeform' }
      : definition;
    debounceRef.current = setTimeout(() => runQuery(effectiveDef), 400);
    return () => clearTimeout(debounceRef.current);
  }, [definition, runQuery]);

  // ---- ⌘K shortcut to open field picker ----
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPicker({ open: true, target: picker.target || 'metric' });
        setTimeout(() => pickerInputRef.current?.focus(), 50);
      }
      if (e.key === 'Escape' && picker.open) setPicker(p => ({ ...p, open: false }));
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [picker.open, picker.target]);

  // ============================ Builder mutations ============================
  const patch = (upd) => setDefinition(d => ({ ...d, ...upd }));
  const openPicker = (target) => { setPicker({ open: true, target }); setPickerQuery(''); setPickerTab(target === 'metric' ? 'metric' : 'dim'); setTimeout(() => pickerInputRef.current?.focus(), 50); };
  const addField = (item, target) => {
    if (item.kind === 'metric') {
      setDefinition(d => d.metrics.includes(item.id) ? d : ({ ...d, metrics: [...d.metrics, item.id] }));
    } else {
      const key = target === 'col' ? 'dimensions_cols' : 'dimensions_rows';
      setDefinition(d => d[key].includes(item.id) ? d : ({ ...d, [key]: [...d[key], item.id] }));
    }
  };
  const removeMetric = (id) => setDefinition(d => ({ ...d, metrics: d.metrics.filter(x => x !== id), sort: d.sort.filter(s => s.field !== id) }));
  const removeRowDim = (id) => setDefinition(d => ({ ...d, dimensions_rows: d.dimensions_rows.filter(x => x !== id) }));
  const removeColDim = (id) => setDefinition(d => ({ ...d, dimensions_cols: d.dimensions_cols.filter(x => x !== id) }));
  const addFilter = () => patch({ filters: [...definition.filters, { field: 'channel', op: 'eq', value: '' }] });
  const updateFilter = (i, f) => patch({ filters: definition.filters.map((x, idx) => idx === i ? { ...x, ...f } : x) });
  const removeFilter = (i) => patch({ filters: definition.filters.filter((_, idx) => idx !== i) });

  // Sort helper: v2 exposes only ONE sort slot (metric + direction).
  const currentSort = definition.sort?.[0] || { field: '', dir: 'desc' };
  const setSort = (field, dir) => patch({ sort: field ? [{ field, dir }] : [] });

  const applyTemplate = (t) => {
    setDefinition(d => ({
      ...d,
      technique: 'pivot',
      dimensions_rows: t.def.rows,
      dimensions_cols: t.def.cols,
      metrics: t.def.metrics,
      sort: [{ field: t.def.metrics[0], dir: 'desc' }],
    }));
  };

  // ============================ Report CRUD ============================
  const doSave = async (asNew = false) => {
    setSaving(true);
    try {
      const payload = { name: reportName, definition, shared: reportShared };
      let saved;
      if (activeReportId && !asNew) {
        const r = await fetch(`${API}/api/exploration/reports/${activeReportId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(await r.text());
        saved = await r.json();
      } else {
        const r = await fetch(`${API}/api/exploration/reports`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(payload),
        });
        if (!r.ok) throw new Error(await r.text());
        saved = await r.json();
      }
      setActiveReportId(saved.report_id);
      const rep = await fetch(`${API}/api/exploration/reports`, { headers: authHeaders() }).then(r => r.json());
      setReportList(rep.reports || []);
      toast.success(asNew ? 'Report saved as new' : 'Report saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };
  const loadReport = (r) => {
    setActiveReportId(r.report_id); setReportName(r.name); setReportShared(!!r.shared);
    setDefinition({ ...emptyDefinition(), ...(r.definition || {}) });
  };
  const newReport = () => {
    setActiveReportId(null); setReportName('New Exploration'); setReportShared(false);
    setDefinition(emptyDefinition());
  };
  const duplicateReport = async () => {
    if (!activeReportId) return;
    try {
      const r = await fetch(`${API}/api/exploration/reports/${activeReportId}/duplicate`, { method: 'POST', headers: authHeaders() });
      const clone = await r.json();
      const rep = await fetch(`${API}/api/exploration/reports`, { headers: authHeaders() }).then(rr => rr.json());
      setReportList(rep.reports || []); loadReport(clone);
      toast.success('Report duplicated');
    } catch { toast.error('Duplicate failed'); }
  };
  const deleteReport = async () => {
    if (!activeReportId) return;
    if (!window.confirm('Delete this exploration?')) return;
    try {
      const r = await fetch(`${API}/api/exploration/reports/${activeReportId}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error();
      const rep = await fetch(`${API}/api/exploration/reports`, { headers: authHeaders() }).then(rr => rr.json());
      setReportList(rep.reports || []); newReport();
      toast.success('Report deleted');
    } catch { toast.error('Delete failed'); }
  };
  const downloadExport = async (format) => {
    try {
      const res = await fetch(`${API}/api/exploration/query/export?format=${format}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(definition),
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeName = (reportName || 'exploration').replace(/[^a-z0-9\-_]/gi, '_');
      a.href = url; a.download = `${safeName}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  // ============================ Derived ============================
  const dimById = useMemo(() => Object.fromEntries(schema.dimensions.map(d => [d.id, d])), [schema.dimensions]);
  const metricById = useMemo(() => Object.fromEntries(schema.metrics.map(m => [m.id, m])), [schema.metrics]);
  const dimLabel = (id) => dimById[id]?.label || id;
  const metricLabel = (id) => metricById[id]?.label || id;
  const relativeAge = useRelativeTime(lastRunAt);

  // Plain-English recap fragments
  const recap = useMemo(() => {
    const parts = [];
    if (definition.metrics.length) {
      const names = definition.metrics.map(metricLabel);
      const last = names.pop();
      parts.push({ label: 'Show', value: names.length ? `${names.join(', ')} and ${last}` : last });
    }
    if (definition.dimensions_rows.length) parts.push({ label: 'Break it down by', value: definition.dimensions_rows.map(dimLabel).join(', ') });
    if (definition.dimensions_cols.length) parts.push({ label: 'Compare across', value: definition.dimensions_cols.map(dimLabel).join(', ') });
    if (definition.filters.length) {
      const bits = definition.filters.map(f => {
        const val = Array.isArray(f.value) ? f.value.join(', ') : (f.value || '');
        return `${dimLabel(f.field) || f.field} ${f.op} "${val}"`;
      });
      parts.push({ label: 'Where', value: bits.join(' AND ') });
    }
    if (currentSort.field) parts.push({ label: 'Sorted by', value: `${metricLabel(currentSort.field)} (${currentSort.dir === 'desc' ? 'High to Low' : 'Low to High'})` });
    return parts;
  }, [definition, schema]); // eslint-disable-line react-hooks/exhaustive-deps

  const builderIsEmpty = !definition.metrics.length && !definition.dimensions_rows.length && !definition.dimensions_cols.length && !definition.filters.length;

  // ============================ Render ============================
  return (
    <div className="min-h-screen flex bg-gray-50" data-testid="exploration-page">
      <Sidebar user={user} currentPage="exploration" />
      <div className="flex-1 flex flex-col">
        <Header user={user} title="Exploration Report" hideGlobalFilters />

        {/* Top bar */}
        <div className="border-b border-gray-200 bg-white px-6 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <select value={activeReportId || ''} onChange={(e) => { const r = reportList.find(x => x.report_id === e.target.value); if (r) loadReport(r); else newReport(); }}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-sm bg-white max-w-xs" data-testid="exploration-report-select">
              <option value="">— New Exploration —</option>
              {reportList.map(r => (
                <option key={r.report_id} value={r.report_id}>
                  {r.name}{r.owned_by_me ? '' : ` (by ${r.owner_email || 'team'})`}{r.shared ? ' • shared' : ''}
                </option>
              ))}
            </select>
            <input type="text" value={reportName} onChange={(e) => setReportName(e.target.value)}
              className="px-3 py-1.5 border border-gray-200 rounded-md text-sm flex-1 min-w-0 max-w-md focus:border-[#32B4AC] focus:outline-none"
              placeholder="Report name" data-testid="exploration-name-input" />
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={reportShared} onChange={(e) => setReportShared(e.target.checked)} data-testid="exploration-shared-toggle" />
              {reportShared ? <Share2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />} Shared with team
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={newReport} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors" data-testid="exploration-new-btn">+ New</button>
            <button onClick={() => doSave(false)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#32B4AC] text-white rounded-md hover:bg-[#2a9d95] disabled:opacity-50 transition-colors" data-testid="exploration-save-btn">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            {activeReportId && (
              <>
                <button onClick={() => doSave(true)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-md hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors" data-testid="exploration-saveas-btn">Save as new</button>
                <button onClick={duplicateReport} className="p-1.5 border border-gray-200 rounded-md hover:border-[#32B4AC] transition-colors" title="Duplicate" data-testid="exploration-duplicate-btn"><Copy className="w-3.5 h-3.5" /></button>
                <button onClick={deleteReport} className="p-1.5 border border-gray-200 rounded-md hover:border-red-400 hover:text-red-500 transition-colors" title="Delete" data-testid="exploration-delete-btn"><Trash2 className="w-3.5 h-3.5" /></button>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Templates (only show when builder is empty) */}
          {builderIsEmpty && (
            <section className="px-6 pt-6" data-testid="templates-gallery">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[#32B4AC]" />
                <h2 className="text-sm font-semibold text-[#0F0F0F]">Popular Templates <span className="text-xs font-normal text-gray-400">— one-click reports</span></h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {TEMPLATES.map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.id} onClick={() => applyTemplate(t)}
                      className="text-left bg-white border border-gray-200 rounded-xl p-3.5 hover:border-[#32B4AC] hover:shadow-sm transition-all group"
                      data-testid={`template-${t.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#32B4AC]/10 text-[#32B4AC] flex items-center justify-center shrink-0 group-hover:bg-[#32B4AC] group-hover:text-white transition-colors">
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-[#0F0F0F] truncate">{t.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t.desc}</div>
                          <div className="text-[11px] text-gray-400 mt-1.5 font-mono">{t.caption}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Smart Builder — Trace AI Analytics Assistant */}
              <SmartBuilder />
            </section>
          )}

          {/* Guided builder + Recap */}
          <section className="px-6 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
              {/* Left: guided builder */}
              <div className="lg:col-span-3 bg-white border border-gray-200 rounded-xl p-5" data-testid="guided-builder">
                <div className="flex items-center gap-2 mb-4">
                  <Compass className="w-4 h-4 text-[#32B4AC]" />
                  <h2 className="text-sm font-semibold text-[#0F0F0F]">Let&apos;s build your report</h2>
                  <div className="ml-auto flex items-center gap-2">
                    <label className="text-xs text-gray-500">Date range</label>
                    <input type="date" value={definition.date_range.start || ''}
                      onChange={(e) => patch({ date_range: { ...definition.date_range, start: e.target.value } })}
                      className="px-2 py-1 border border-gray-200 rounded text-xs focus:border-[#32B4AC] focus:outline-none" data-testid="date-start" />
                    <span className="text-xs text-gray-300">→</span>
                    <input type="date" value={definition.date_range.end || ''}
                      onChange={(e) => patch({ date_range: { ...definition.date_range, end: e.target.value } })}
                      className="px-2 py-1 border border-gray-200 rounded text-xs focus:border-[#32B4AC] focus:outline-none" data-testid="date-end" />
                    {[{ l: '7d', d: 7 }, { l: '30d', d: 30 }, { l: '90d', d: 90 }].map(p => (
                      <button key={p.l} onClick={() => patch({ date_range: { start: daysAgoISO(p.d), end: todayISO() } })}
                        className="px-1.5 py-0.5 text-[10px] border border-gray-200 rounded hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors">{p.l}</button>
                    ))}
                  </div>
                </div>

                {/* Step 1 — Metrics */}
                <BuilderStep n={1} label="What do you want to analyze?">
                  <ChipRow>
                    {definition.metrics.map(id => (
                      <Chip key={id} label={metricLabel(id)} onRemove={() => removeMetric(id)} testid={`chip-metric-${id}`} />
                    ))}
                    <AddChip onClick={() => openPicker('metric')} label="Add metric" testid="add-metric" />
                  </ChipRow>
                </BuilderStep>

                {/* Step 2 — Row dimension(s) */}
                <BuilderStep n={2} label="Break it down by">
                  <ChipRow>
                    {definition.dimensions_rows.map(id => (
                      <Chip key={id} label={dimLabel(id)} onRemove={() => removeRowDim(id)} testid={`chip-row-${id}`} />
                    ))}
                    <AddChip onClick={() => openPicker('row')} label="Add dimension" testid="add-row-dim" />
                  </ChipRow>
                </BuilderStep>

                {/* Step 3 — Column dimension(s) — pivot */}
                <BuilderStep n={3} label="Compare across" hint="Optional — leave empty for a simple table.">
                  <ChipRow>
                    {definition.dimensions_cols.map(id => (
                      <Chip key={id} label={dimLabel(id)} onRemove={() => removeColDim(id)} testid={`chip-col-${id}`} />
                    ))}
                    <AddChip onClick={() => openPicker('col')} label="Add dimension" testid="add-col-dim" />
                  </ChipRow>
                </BuilderStep>

                {/* Step 4 — Filters */}
                <BuilderStep n={4} label="Filter your data" hint="Only rows matching all filters are included.">
                  <div className="space-y-1.5">
                    {definition.filters.map((f, i) => (
                      <FilterRow key={i} filter={f} dims={schema.dimensions}
                        onChange={(nf) => updateFilter(i, nf)} onRemove={() => removeFilter(i)} />
                    ))}
                    <button onClick={addFilter}
                      className="inline-flex items-center gap-1 px-2.5 py-1 border border-dashed border-gray-300 rounded-md text-xs text-gray-500 hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors" data-testid="add-filter">
                      <Plus className="w-3 h-3" /> Add filter
                    </button>
                  </div>
                </BuilderStep>

                {/* Step 5 — Sort */}
                <BuilderStep n={5} label="Sort by" hint="Optional — defaults to the first metric.">
                  <div className="flex items-center gap-2">
                    <select value={currentSort.field} onChange={(e) => setSort(e.target.value, currentSort.dir || 'desc')}
                      className="px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:border-[#32B4AC] focus:outline-none" data-testid="sort-field">
                      <option value="">(default)</option>
                      {definition.metrics.map(id => <option key={id} value={id}>{metricLabel(id)}</option>)}
                    </select>
                    <select value={currentSort.dir || 'desc'} onChange={(e) => setSort(currentSort.field, e.target.value)} disabled={!currentSort.field}
                      className="px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:border-[#32B4AC] focus:outline-none disabled:opacity-50" data-testid="sort-dir">
                      <option value="desc">High to Low</option>
                      <option value="asc">Low to High</option>
                    </select>
                    <select value={definition.limit} onChange={(e) => patch({ limit: Number(e.target.value) })}
                      className="ml-auto px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:border-[#32B4AC] focus:outline-none" data-testid="topn-select">
                      {[10, 25, 50, 100, 250, 500, 1000, 5000].map(n => <option key={n} value={n}>Top {n}</option>)}
                    </select>
                  </div>
                </BuilderStep>

                <button onClick={() => openPicker('metric')}
                  className="mt-2 flex items-center gap-1 text-xs text-[#32B4AC] hover:underline">
                  <Search className="w-3 h-3" /> Search fields <kbd className="ml-1 px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded text-[10px] text-gray-500 font-mono">⌘K</kbd>
                </button>
              </div>

              {/* Right: recap */}
              <div className="lg:col-span-2 bg-gradient-to-br from-[#32B4AC]/5 to-white border border-[#32B4AC]/20 rounded-xl p-5" data-testid="recap-panel">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-md bg-[#32B4AC]/10 text-[#32B4AC] flex items-center justify-center">
                    <ArrowUp className="w-3.5 h-3.5 rotate-45" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#0F0F0F]">Your report</h3>
                </div>
                {recap.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">Pick a metric and a dimension to get started, or apply a template above.</p>
                ) : (
                  <ul className="space-y-3 text-sm">
                    {recap.map((r, i) => (
                      <li key={i}>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{r.label}</div>
                        <div className="text-[#0F0F0F] mt-0.5 leading-snug break-words">{r.value}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-4 pt-3 border-t border-[#32B4AC]/20 text-[11px] text-gray-500 flex items-center gap-1.5">
                  <Zap className="w-3 h-3 text-[#32B4AC]" />
                  <span>Tip: click any field to add or remove it — no drag needed.</span>
                </div>
              </div>
            </div>
          </section>

          {/* Result */}
          <section className="px-6 pb-8" data-testid="result-section">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Filter chips + date bar */}
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-100">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-xs text-gray-700">
                  <Calendar className="w-3 h-3" />{definition.date_range.start || '—'} → {definition.date_range.end || '—'}
                </span>
                {definition.filters.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#32B4AC]/10 text-xs text-[#0F0F0F]">
                    <FilterIcon className="w-3 h-3 text-[#32B4AC]" />
                    {dimLabel(f.field) || f.field} {f.op} &quot;{Array.isArray(f.value) ? f.value.join(', ') : (f.value || '')}&quot;
                    <button onClick={() => removeFilter(i)} className="ml-0.5 text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </span>
                ))}
                {definition.filters.length > 0 && (
                  <button onClick={() => patch({ filters: [] })} className="text-[11px] text-gray-500 hover:text-[#32B4AC] hover:underline ml-1">Reset</button>
                )}
                <div className="ml-auto flex items-center gap-2 text-[11px] text-gray-400">
                  {loading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Updating…</> : lastRunAt ? <>Updated {relativeAge}</> : null}
                  <button onClick={() => runQuery(definition)} disabled={loading}
                    className="p-1 border border-gray-200 rounded hover:border-[#32B4AC] hover:text-[#32B4AC] disabled:opacity-50 transition-colors"
                    title="Refresh" data-testid="refresh-btn">
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-auto max-h-[70vh]">
                {error && <div className="p-8 text-center text-sm text-red-500" data-testid="query-error">Error: {error}</div>}
                {!error && !result && !loading && (
                  <div className="p-8 text-center text-sm text-gray-400">
                    Add at least one metric and one dimension to see results.
                  </div>
                )}
                {!error && result && result.rows && result.rows.length === 0 && (
                  <div className="p-8 text-center text-sm text-gray-400">No data matches the current filters and date range.</div>
                )}
                {!error && result && result.rows && result.rows.length > 0 && (
                  <ResultTable result={result} definition={definition} />
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 text-[11px] text-gray-500">
                <span>{result ? `Showing ${result.rows?.length || 0} of ${result.row_count || 0} rows` : '—'}</span>
                <div className="relative inline-block">
                  <ExportDropdown onExport={downloadExport} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Field picker modal */}
      {picker.open && (
        <FieldPicker
          schema={schema}
          selectedMetrics={definition.metrics}
          selectedRowDims={definition.dimensions_rows}
          selectedColDims={definition.dimensions_cols}
          target={picker.target}
          query={pickerQuery} setQuery={setPickerQuery}
          tab={pickerTab} setTab={setPickerTab}
          onAdd={(item) => addField(item, picker.target)}
          onClose={() => setPicker({ open: false, target: picker.target })}
          inputRef={pickerInputRef}
        />
      )}
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================
const BuilderStep = ({ n, label, hint, children }) => (
  <div className="mb-4">
    <div className="flex items-baseline gap-2 mb-2">
      <span className="w-5 h-5 rounded-full bg-[#32B4AC] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{n}</span>
      <div className="text-sm font-semibold text-[#0F0F0F]">{label}</div>
      {hint && <div className="text-[11px] text-gray-400 italic ml-1">{hint}</div>}
    </div>
    <div className="pl-7">{children}</div>
  </div>
);

const ChipRow = ({ children }) => <div className="flex flex-wrap gap-1.5">{children}</div>;

const Chip = ({ label, onRemove, testid }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#32B4AC]/10 text-[#0F0F0F] rounded-md text-xs font-medium border border-[#32B4AC]/20" data-testid={testid}>
    <span className="max-w-[180px] truncate">{label}</span>
    <button onClick={onRemove} className="text-gray-400 hover:text-red-500 ml-0.5"><X className="w-3 h-3" /></button>
  </span>
);

const AddChip = ({ onClick, label, testid }) => (
  <button type="button" onClick={onClick}
    className="inline-flex items-center gap-1 px-2.5 py-1 border border-dashed border-gray-300 rounded-md text-xs text-gray-500 hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors"
    data-testid={testid}>
    <Plus className="w-3 h-3" /> {label}
  </button>
);

const FilterRow = ({ filter, dims, onChange, onRemove }) => (
  <div className="flex items-center gap-1 p-1.5 bg-gray-50/60 border border-gray-200 rounded-md">
    <select value={filter.field} onChange={(e) => onChange({ field: e.target.value })}
      className="px-2 py-1 border border-gray-200 rounded text-xs bg-white flex-1 min-w-0">
      {dims.map(d => <option key={d.id} value={d.field || d.id}>{d.label}</option>)}
    </select>
    <select value={filter.op} onChange={(e) => onChange({ op: e.target.value })}
      className="px-2 py-1 border border-gray-200 rounded text-xs bg-white">
      <option value="eq">=</option>
      <option value="ne">≠</option>
      <option value="in">in</option>
      <option value="contains">contains</option>
    </select>
    <input type="text" value={Array.isArray(filter.value) ? filter.value.join(',') : (filter.value || '')}
      onChange={(e) => onChange({ value: filter.op === 'in' ? e.target.value.split(',').map(s => s.trim()).filter(Boolean) : e.target.value })}
      placeholder={filter.op === 'in' ? 'v1, v2, …' : 'value'}
      className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs focus:border-[#32B4AC] focus:outline-none" />
    <button onClick={onRemove} className="p-1 rounded hover:bg-red-50 text-red-500"><X className="w-3 h-3" /></button>
  </div>
);

// ---- Field picker (search + tabs) ----
const FieldPicker = ({ schema, selectedMetrics, selectedRowDims, selectedColDims, target, query, setQuery, tab, setTab, onAdd, onClose, inputRef }) => {
  const q = query.trim().toLowerCase();
  const filter = (list) => q ? list.filter(x => x.label.toLowerCase().includes(q) || x.id.toLowerCase().includes(q)) : list;
  const dims = tab === 'metric' ? [] : filter(schema.dimensions.map(d => ({ ...d, kind: 'dim' })));
  const metrics = tab === 'dim' ? [] : filter(schema.metrics.map(m => ({ ...m, kind: 'metric' })));
  const isSelected = (item) => item.kind === 'metric' ? selectedMetrics.includes(item.id)
    : target === 'col' ? selectedColDims.includes(item.id) : selectedRowDims.includes(item.id);
  const shelfLabel = target === 'metric' ? 'Metrics' : target === 'col' ? 'Compare across' : 'Break it down by';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm pt-20" onClick={onClose} data-testid="field-picker">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Search className="w-4 h-4 text-gray-400" />
          <input ref={inputRef} type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fields…" className="flex-1 text-sm bg-transparent focus:outline-none" data-testid="field-picker-search" />
          <kbd className="text-[10px] px-1.5 py-0.5 bg-gray-100 border border-gray-200 rounded font-mono text-gray-500">Esc</kbd>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <div className="flex items-center gap-1 px-4 pt-2 border-b border-gray-100">
          {[{ id: 'all', l: 'All' }, { id: 'dim', l: 'Dimensions' }, { id: 'metric', l: 'Metrics' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-md transition-colors ${tab === t.id ? 'bg-[#32B4AC]/10 text-[#32B4AC] border-b-2 border-[#32B4AC]' : 'text-gray-500 hover:text-[#0F0F0F]'}`}
              data-testid={`picker-tab-${t.id}`}>
              {t.l}
            </button>
          ))}
          <span className="ml-auto text-[10px] text-gray-400 py-1.5">Adding to <b>{shelfLabel}</b></span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {dims.length > 0 && (
            <div className="mb-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 px-2 py-1">Dimensions <span className="text-gray-300">({dims.length})</span></div>
              {dims.map(d => {
                const Icon = DIM_ICON[d.id] || Layers;
                const sel = isSelected(d);
                return (
                  <button key={d.id} onClick={() => { if (!sel) onAdd(d); }} disabled={sel}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left ${sel ? 'text-gray-300 cursor-default' : 'text-[#0F0F0F] hover:bg-[#32B4AC]/10'}`}
                    data-testid={`picker-item-${d.id}`}>
                    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="flex-1 truncate">{d.label}</span>
                    {sel && <span className="text-[10px] text-gray-400">Added</span>}
                  </button>
                );
              })}
            </div>
          )}
          {metrics.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 px-2 py-1">Metrics <span className="text-gray-300">({metrics.length})</span></div>
              {metrics.map(m => {
                const Icon = METRIC_ICON[m.id] || BarChart3;
                const sel = isSelected(m);
                return (
                  <button key={m.id} onClick={() => { if (!sel) onAdd(m); }} disabled={sel}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left ${sel ? 'text-gray-300 cursor-default' : 'text-[#0F0F0F] hover:bg-[#32B4AC]/10'}`}
                    data-testid={`picker-item-${m.id}`}>
                    <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                    <span className="flex-1 truncate">{m.label}</span>
                    {sel && <span className="text-[10px] text-gray-400">Added</span>}
                  </button>
                );
              })}
            </div>
          )}
          {dims.length === 0 && metrics.length === 0 && (
            <div className="p-4 text-center text-xs text-gray-400 italic">No matches for &quot;{query}&quot;</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Export dropdown ----
const ExportDropdown = ({ onExport }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-md text-xs hover:border-[#32B4AC] hover:text-[#32B4AC] transition-colors"
        data-testid="export-btn">
        <Download className="w-3 h-3" /> Export <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[110px] z-10">
          <button onClick={() => { onExport('csv'); setOpen(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50" data-testid="export-csv">CSV (.csv)</button>
          <button onClick={() => { onExport('xlsx'); setOpen(false); }} className="w-full px-3 py-1.5 text-left text-xs hover:bg-gray-50" data-testid="export-xlsx">Excel (.xlsx)</button>
        </div>
      )}
    </div>
  );
};

// ---- Result table (pivot = nested rows, one column per metric) ----
const ResultTable = ({ result, definition }) => {
  const cols = result.columns || [];
  const rows = result.rows || [];
  const totals = result.totals || {};
  const isPivot = result.technique === 'pivot';

  const dimCols = cols.filter(c => c.kind === 'dimension');
  const metricCols = cols.filter(c => c.kind === 'metric');

  // In pivot mode we render one column per *metric* (Revenue, Spend, ROI, …)
  // and stack each pivot_col_value (e.g. 2026-07 / 2026-08) as a nested child
  // row underneath the dimension row. This is the standard "compare across"
  // pivot layout — one metric column set, compare values live in rows.
  const uniqueMetrics = [];         // [{ metric_label, format }]
  const pivotValues = [];           // ['2026-07', '2026-08', …] preserving order
  const colByMetricAndPivot = {};   // metric_label -> pivot_value -> col.id
  if (isPivot) {
    const seenMetric = new Set();
    const seenPivot = new Set();
    metricCols.forEach(c => {
      const ml = c.metric_label || c.label;
      const pv = c.pivot_col_value || '';
      if (!seenMetric.has(ml)) {
        seenMetric.add(ml);
        uniqueMetrics.push({ metric_label: ml, format: c.format });
      }
      if (!seenPivot.has(pv)) {
        seenPivot.add(pv);
        pivotValues.push(pv);
      }
      colByMetricAndPivot[ml] = colByMetricAndPivot[ml] || {};
      colByMetricAndPivot[ml][pv] = c.id;
    });
  }

  const displayCols = isPivot
    ? [...dimCols, ...uniqueMetrics.map(m => ({ id: m.metric_label, label: m.metric_label, format: m.format }))]
    : [...dimCols, ...metricCols.map(c => ({ id: c.id, label: c.metric_label || c.label, format: c.format }))];

  return (
    <table className="min-w-full text-sm" data-testid="result-table">
      <thead>
        <tr className="border-b border-gray-200 bg-gray-50/80">
          <th className="text-left py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap w-10">#</th>
          {displayCols.map((c, i) => (
            <th key={c.id || i} className={`py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap ${i < dimCols.length ? 'text-left' : 'text-right'}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => {
          if (!isPivot) {
            return (
              <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
                <td className="py-2 px-3 text-xs text-gray-400 tabular-nums">{idx + 1}</td>
                {dimCols.map(c => (
                  <td key={c.id} className="py-2 px-3 text-left" title={String(r[c.id] ?? '')}>{r[c.id] ?? '—'}</td>
                ))}
                {metricCols.map(c => (
                  <td key={c.id} className="py-2 px-3 text-right tabular-nums whitespace-nowrap">
                    {fmtValue(r[c.id], c.format)}
                  </td>
                ))}
              </tr>
            );
          }
          // Pivot: render the dimension row (blank metric cells) then a child
          // row per pivot_col_value with that pivot's metric values.
          return (
            <React.Fragment key={idx}>
              <tr className="border-b border-gray-100 bg-gray-50/60" data-testid={`result-parent-row-${idx}`}>
                <td className="py-2 px-3 text-xs text-gray-400 tabular-nums align-top">{idx + 1}</td>
                {dimCols.map(c => (
                  <td key={c.id} className="py-2 px-3 text-left font-semibold text-[#0F0F0F] align-top" title={String(r[c.id] ?? '')}>{r[c.id] ?? '—'}</td>
                ))}
                {uniqueMetrics.map(m => <td key={m.metric_label} className="py-2 px-3 align-top"></td>)}
              </tr>
              {pivotValues.map((pv, pi) => (
                <tr key={`${idx}-${pv}`} className="border-b border-gray-100 hover:bg-gray-50/40 transition-colors" data-testid={`result-child-row-${idx}-${pi}`}>
                  <td className="py-2 px-3"></td>
                  {dimCols.map((c, ci) => (
                    <td key={c.id} className={`py-2 px-3 text-left text-[12.5px] text-gray-600 ${ci === 0 ? 'pl-10' : ''}`}>
                      {ci === 0 ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-gray-300 text-xs">└─</span>
                          <span className="font-medium text-[#0F766E]">{pv || '—'}</span>
                        </span>
                      ) : ''}
                    </td>
                  ))}
                  {uniqueMetrics.map(m => {
                    const colId = colByMetricAndPivot[m.metric_label]?.[pv];
                    const value = colId != null ? r[colId] : null;
                    return (
                      <td key={m.metric_label} className="py-2 px-3 text-right tabular-nums whitespace-nowrap text-gray-700">
                        {fmtValue(value, m.format)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </React.Fragment>
          );
        })}
        {!isPivot && totals && Object.keys(totals).length > 0 && (
          <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
            <td className="py-2.5 px-3 text-xs text-gray-500 uppercase tracking-wider">Total</td>
            {dimCols.map((c, i) => <td key={c.id} className="py-2.5 px-3">{i === 0 ? '' : ''}</td>)}
            {metricCols.map(c => (
              <td key={c.id} className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap text-[#0F0F0F]">
                {fmtValue(totals[c.id], c.format)}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
};
